import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { IUserData } from '@topcon/twc-angular';

import { ResponseErrorCodes, TokenResponseData, TokenUser, User, UserResponseData } from '@mirage-projects/cw-models';

import { MagnetService } from '../../abstract-classes/magnet.service';
import { AclControlService, AUTHORIZATION_ADMIN_ROLE } from '../acl-permissions/acl-control.service';
import { EntryPoint } from '../entry-point/entry-point.enum';
import { EntryPointService } from '../entry-point/entry-point.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { LogService } from '../log/log.service';
import { SiteTokenResponse } from './token-response';

export interface AppLogoutParams {
    error?: ResponseErrorCodes;
    detail?: string;
    detailList?: string;
}

export interface LogoutErrorDetails {
    aliveUsers: Array<{ user: string }>;
}

/** This service has all the business logic related to authorization */
@Injectable({ providedIn: 'root' })
export class AuthorizationService {
    readonly SUPPORT_TOKEN_CW = 'supportToken';
    readonly CURRENT_USER = 'currentUser';
    readonly CURRENT_SUPORT_USER = 'currentSupportUser';
    readonly TWC_USER_ACCESS = 'twc-user-access';
    readonly TWC_USER_DATA = 'twc-user-data';
    readonly ANONYMOUS_USER = 'anonymous';
    readonly ANONYMOUS_TOKEN_PREFIX = 'anonymous-';
    readonly HOME_PATH = this.magnetService.isSite() ? '/projects' : '/home';
    /** Key for localStorage dashboard messages status */
    readonly DASHBOARD_MESSAGE_KEY = 'closed-dashboard-messages';

    TOKEN_CW = 'token';
    userLogged!: User;
    userLoggedSupport!: User;
    capabilities!: string[];

    constructor(
        private localStorageService: LocalStorageService,
        private http: HttpClient,
        private entryPointService: EntryPointService,
        private aclControlService: AclControlService,
        private magnetService: MagnetService,
        private router: Router,
        private logService: LogService
    ) {}

    setAnonymousProject(projectId: number): void {
        this.TOKEN_CW = `${this.ANONYMOUS_TOKEN_PREFIX}${projectId}`;
    }

    appLogout(params: AppLogoutParams = {}): void {
        if (params.error) {
            this.logoutWithError(params.error);
        } else {
            this.logout();
        }
    }

    redirectToHome(params?: AppLogoutParams): void {
        this.router.navigate([this.HOME_PATH], { queryParams: params });
    }

    clearCredentials(): void {
        this.localStorageService.removeItem(this.CURRENT_USER);
        this.localStorageService.removeItem(this.TWC_USER_DATA);
        this.localStorageService.removeItem(this.TOKEN_CW);
        this.localStorageService.removeItem('type');
        this.localStorageService.removeItem('mainNotifications');
        this.localStorageService.removeItem('app');
        this.localStorageService.removeItem(this.DASHBOARD_MESSAGE_KEY);

        this.clearCurrentSupportUser();
    }

    clearCurrentSupportUser(): void {
        this.clearSupportUser();
        this.clearSupportToken();
    }

    isUserLogged(): boolean {
        if (this.magnetService.isSite()) {
            return !!(this.getToken() && this.userLogged);
        } else {
            return !!(this.getToken() && this.getTokenTWC() && this.getUserLoggedTWC());
        }
    }

    getToken(): string | null {
        return this.localStorageService.getItem(this.TOKEN_CW);
    }

    getTokenTWC(): string | null {
        const userAccessStr = this.localStorageService.getItem(this.TWC_USER_ACCESS);
        if (!userAccessStr) {
            return null;
        }
        return JSON.parse(userAccessStr)?.access_token;
    }

    getUserLoggedTWC(): IUserData | null {
        const userDataStr = this.localStorageService.getItem(this.TWC_USER_DATA);
        if (!userDataStr) {
            return null;
        }
        return JSON.parse(userDataStr);
    }

    setToken(token: string): void {
        this.localStorageService.setItem(this.TOKEN_CW, token, true);
    }

    getCurrentUser(): Partial<User> {
        const storedUserJson = this.localStorageService.getItem(this.CURRENT_USER) ?? 'null';
        const storedUser: Partial<User> = JSON.parse(storedUserJson);
        return storedUser;
    }

    /**
     * Logs the user in the system and loads the uaser ACLs
     * @param user User object to be logged in the system
     */
    setUserLogged(user: Partial<User>): void {
        const storedUser = this.getCurrentUser();

        if (!this.isUserIdAnonymous(user.userId) && storedUser?.username !== user.userId) {
            this.logService.error('AuthorizationService.setUserLogged: Credentials mismatch');
            this.logout();
        }

        if (this.isUserIdAnonymous(user.userId)) {
            this.userLogged = user as User;
        } else {
            this.userLogged = {
                ...storedUser,
                ...user,
            } as User;
        }

        this.setCurrentUser(this.userLogged);
        // When set the logged user, reload Acl config
        this.aclControlService.loadUserACLs(this.userLogged);
    }

    /**
     * Sets the current user object in the storage without more actions
     * @param user
     */
    setCurrentUser(user: Partial<User>): void {
        if (!this.isUserIdAnonymous(user.userId)) {
            this.localStorageService.setItem(this.CURRENT_USER, user, true);
        }
    }

    getUserLogged(): User {
        return this.userLogged;
    }

    isUserLoggedAdmin(): boolean {
        const userLogged = this.getUserLogged();
        return userLogged.role === AUTHORIZATION_ADMIN_ROLE;
    }

    getAuthType(): string {
        return this.magnetService.isSite() ? 'JWT' : 'Bearer';
    }

    loginCRE(userName: string, password: string): Observable<SiteTokenResponse> {
        const token = this.getToken();
        if (token) {
            return new BehaviorSubject<SiteTokenResponse>({ token, alreadyLoged: true });
        }

        const entryPoint = this.entryPointService.getResource(EntryPoint.LOGIN);
        return this.http.post<SiteTokenResponse>(`${entryPoint}`, { username: userName, password: password }).pipe(
            map((response) => {
                return { ...response, alreadyLoged: false };
            }),
            catchError((err) => throwError(err))
        );
    }

    saveSupportToken(): void {
        this.localStorageService.setItem(this.SUPPORT_TOKEN_CW, this.getToken(), true);
    }

    clearSupportToken(): void {
        this.localStorageService.removeItem(this.SUPPORT_TOKEN_CW);
    }

    saveSupportUser(): void {
        this.userLoggedSupport = this.userLogged;
        this.localStorageService.setItem(this.CURRENT_SUPORT_USER, this.userLoggedSupport, true);
    }

    clearSupportUser(): void {
        this.localStorageService.removeItem(this.CURRENT_SUPORT_USER);
    }

    getSupportToken(): string | null {
        return this.localStorageService.getItem(this.SUPPORT_TOKEN_CW);
    }

    getSupportUser(): User | null {
        const userDataStr = this.localStorageService.getItem(this.CURRENT_SUPORT_USER);
        if (!userDataStr) {
            return null;
        }
        return JSON.parse(userDataStr);
    }

    /**
     * Checks if current user is support or is a support user active
     */
    isSupportUser(): boolean {
        const currentSupportUser = this.getSupportUser();
        if (currentSupportUser) {
            return !!currentSupportUser.isSupport;
        }

        if (this.userLogged) {
            return !!this.userLogged.isSupport;
        }
        return false;
    }

    isCurrentUserAnonymous(): boolean {
        return this.isUserIdAnonymous(this.userLogged.userId);
    }

    /**
     * This function is for shared projects. It checks if an action can be performed.
     */
    isCapabilityAvailable(capability: string): boolean {
        const available = !this.isCurrentUserAnonymous();
        if (!this.capabilities?.length) {
            return available;
        }
        const existCapability = this.capabilities.some((cap) => cap === capability);

        return existCapability ?? available;
    }

    /**
     * Converts the TokenUser from the get-token request to a User type compatible with PointCloudViz
     * @param tokenUser
     */
    tokenUserToUser(tokenUser: TokenUser): Partial<User> {
        const { email, isSupport, firstname, language, lastname, role, id } = tokenUser;
        const fullName = `${firstname} ${lastname}`;
        const firstName = firstname;
        const lastName = lastname;
        const uid = -1;
        const isAdmin = false;
        const isActive = true;
        const username = id;
        const userId = id;
        const status: 'user' | 'administrator' = isAdmin ? 'administrator' : 'user';
        const user = {
            email,
            firstName,
            fullName,
            language,
            isSupport,
            lastName,
            userId,
            username,
            role,
            uid,
            isActive,
            status,
        };
        return user;
    }

    /**
     * Converts the TokenUser from the users/ request to a User type compatible with PointCloudViz
     * @param tokenUser
     */
    userResponseToUser(userData: UserResponseData): Partial<User> {
        const {
            acls,
            allAcls,
            companyId,
            createdById,
            createdByName,
            groups,
            language,
            permissions,
            allPermissions,
            role,
            status,
            userId,
            userFirstName,
            userLastName,
            userName,
            userUserName,
            alive,
        } = userData;

        const isActive = status === 'Active';
        const username = userId;
        const firstName = userFirstName;
        const lastName = userLastName;
        const fullName = userName;
        const email = userUserName;

        const result = {
            acls,
            allAcls,
            createdById,
            createdByName,
            companyId,
            groups,
            language,
            permissions,
            allPermissions,
            alive,

            isActive,
            userId,
            role,
            username,
            firstName,
            lastName,
            fullName,
            email,
        };
        return result;
    }

    setCapabilities(capabilities: string[]): void {
        this.capabilities = capabilities;
    }

    getTokenByCode(code: string): Observable<TokenResponseData> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.GET_TOKEN);
        const appName = this.magnetService.getAppName();
        const url = `${entryPoint}?channel=web&app=${appName}&code=${code}`;
        return this.http.get<any>(url);
    }

    isUserIdAnonymous(userId: string | undefined): boolean {
        return userId?.includes(this.ANONYMOUS_USER) ?? false;
    }

    private logoutWithError(code: ResponseErrorCodes): void {
        const errorCode: AppLogoutParams = { error: code };

        this.logout(errorCode);
    }

    private logout(obj?: AppLogoutParams): void {
        this.clearCredentials();
        this.redirectToHome(obj);
    }
}
